import { MCPStatusIndicator } from "@/components/mcp/MCPStatusIndicator";
import { Badge } from "@/components/ui/Badge";
import { Icon } from "@/components/ui/Icon";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { CopyButton } from "@/components/ui/CopyButton";
import { Text } from "@/components/ui/Text";
import { useProject } from "@/contexts/Auth";
import { useSlugs } from "@/contexts/Sdk";
import { useActiveDeployment } from "@/hooks/toolTypes";
import { dateTimeFormatters } from "@/lib/dates";
import { useListAssets } from "@gram/client/react-query/listAssets.js";
import { useListToolsets } from "@gram/client/react-query/listToolsets.js";
import { useRoutes } from "@/routes";
import { cn } from "@/lib/utils";
import { Download, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { downloadSourceFile, sourceDownloadFilename } from "./sourceDownload";
import { useSourceTools } from "./useSourceQueries";

// Sizes are shown to give a sense of scale, not for accounting, so a single
// significant decimal is enough.
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

function formatMemory(mib: number): string {
  if (mib < 1024) return `${mib} MiB`;
  const gib = mib / 1024;
  return Number.isInteger(gib) ? `${gib} GiB` : `${gib.toFixed(1)} GiB`;
}

// Mirror server/internal/constants/functions.go — applied at deploy time when
// the per-source value is NULL.
const DEFAULT_FUNCTION_MEMORY_MIB = 1024;
const DEFAULT_FUNCTION_SCALE = 2;

/** One labelled fact: label left, value right, in both surfaces. */
/**
 * The MCP servers a source's tools are part of.
 *
 * Hosted servers are toolsets, and a toolset names its tools by URN, so the
 * link from a source to the servers built from it runs through the tools it
 * produced. Servers backed by mcp_servers rows carry no tools and so can't be
 * built from a source.
 */
function SourceServersPanel({
  toolUrns,
  isPage,
}: {
  toolUrns: string[];
  isPage: boolean;
}): React.JSX.Element | null {
  const routes = useRoutes();
  // A failed server list must not take the source page down with it, nor
  // read as "no servers": the tools and file above are still worth showing.
  const { data, isLoading, isError } = useListToolsets(undefined, undefined, {
    throwOnError: false,
  });

  const servers = useMemo(() => {
    if (toolUrns.length === 0) return [];
    const urns = new Set(toolUrns);
    return (data?.toolsets ?? []).filter((toolset) =>
      toolset.toolUrns?.some((urn) => urns.has(urn)),
    );
  }, [data, toolUrns]);

  if (!isPage) {
    // Cached servers survive a failed refetch, as they do on the page: only
    // an empty list says the failure was the reason.
    if (servers.length === 0) {
      return isError ? (
        <Text small muted>
          Couldn&apos;t load the servers this source is used in.
        </Text>
      ) : null;
    }
    return (
      <div className="flex flex-col gap-3">
        <Text className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          Used in
        </Text>
        <ul className="flex flex-col gap-1">
          {servers.map((toolset) => (
            <li key={toolset.id}>
              <routes.toolset.Link params={[toolset.slug]} className="text-sm">
                {toolset.name}
              </routes.toolset.Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <Card.Dashboard
      title="MCP servers"
      tooltip="The hosted servers that carry tools generated from this source."
      bodyClassName={servers.length === 0 ? undefined : "p-0"}
      action={
        servers.length > 0 ? (
          <Text muted className="text-xs">
            {`${servers.length} server${servers.length === 1 ? "" : "s"}`}
          </Text>
        ) : undefined
      }
    >
      {servers.length === 0 ? (
        <Text muted small>
          {isError
            ? "Couldn't load the project's servers. Reload to try again."
            : isLoading
              ? "Loading servers\u2026"
              : "No server carries this source's tools yet. Build one from it to expose them."}
        </Text>
      ) : (
        <ul className="divide-border divide-y">
          {servers.map((toolset) => {
            const count = toolset.toolUrns?.length ?? 0;
            return (
              <li
                key={toolset.id}
                className="flex items-center justify-between gap-4 px-6 py-3"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <routes.toolset.Link
                    params={[toolset.slug]}
                    className="truncate text-sm font-medium"
                  >
                    {toolset.name}
                  </routes.toolset.Link>
                  <Text muted className="truncate font-mono text-xs">
                    {toolset.slug}
                  </Text>
                </div>
                <div className="flex shrink-0 items-center gap-4">
                  {/* The same status dot the MCP page uses, so visibility
                      reads the same color here as it does there. */}
                  <MCPStatusIndicator
                    mcpEnabled={toolset.mcpEnabled}
                    mcpIsPublic={toolset.mcpIsPublic}
                    size="sm"
                  />
                  <Text muted className="text-xs">
                    {`${count} tool${count === 1 ? "" : "s"}`}
                  </Text>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card.Dashboard>
  );
}

function SourceFact({
  label,
  isPage,
  tooltip,
  children,
}: {
  label: string;
  isPage: boolean;
  /** Explains a label that names a Speakeasy concept rather than a file fact. */
  tooltip?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4",
        // Banded on the page, where the rows run the full width and the eye
        // needs help carrying a label across to its value.
        isPage && "odd:bg-muted/20 px-6 py-3",
      )}
    >
      <dt className="text-muted-foreground flex items-center gap-1.5 text-xs">
        {label}
        {tooltip && (
          <SimpleTooltip tooltip={tooltip}>
            <button
              type="button"
              aria-label={`About ${label}`}
              className="hover:text-foreground inline-flex cursor-help items-center"
            >
              <Icon name="info" className="size-3.5" />
            </button>
          </SimpleTooltip>
        )}
      </dt>
      <dd className="min-w-0 truncate font-mono text-xs">{children}</dd>
    </div>
  );
}

/**
 * What one source is and what it produced, for the panel beside the
 * create-from-source flow.
 *
 * Keyed by asset id alone: the panel outlives the page that opened it, so it
 * refetches rather than being handed the source. Both queries are already warm
 * from the page, so this costs nothing in practice.
 */
export function SourceDetailPanel({
  sourceKind,
  assetId,
}: {
  sourceKind: "openapi" | "function";
  assetId: string;
}): React.JSX.Element {
  return (
    <div className="px-6 pt-5 pb-8">
      <SourceDetail sourceKind={sourceKind} assetId={assetId} />
    </div>
  );
}

/**
 * The body of the panel, which the source's own page renders too.
 *
 * Split out so a source reads the same whether it is met in the sheet beside
 * the create flow or at its own URL, and so only the padding differs.
 */
export function SourceDetail({
  sourceKind,
  assetId,
  variant = "panel",
}: {
  sourceKind: "openapi" | "function";
  assetId: string;
  /**
   * "panel" is the narrow column in the sheet: facts stacked label-left,
   * value-right, and a tool list that scrolls in place. "page" has room to
   * lay the same facts across and let the tools run down the page.
   */
  variant?: "panel" | "page";
}): React.JSX.Element {
  const routes = useRoutes();
  const { data: deploymentResult } = useActiveDeployment();
  const {
    tools,
    toolUrns,
    isLoading,
    isError: isToolsError,
  } = useSourceTools(sourceKind, assetId);
  // The deployment names the source; the asset carries the file itself.
  const { data: assetsResult } = useListAssets();

  const deployment = deploymentResult?.deployment;
  // Looked up by kind so the function branch keeps its own type: only
  // functions carry a runtime and sizing, which the facts below read.
  const functionAsset =
    sourceKind === "function"
      ? deployment?.functionsAssets?.find((a) => a.id === assetId)
      : undefined;
  const asset =
    sourceKind === "openapi"
      ? deployment?.openapiv3Assets?.find((a) => a.id === assetId)
      : functionAsset;

  const file = assetsResult?.assets?.find((a) => a.id === asset?.assetId);

  const isPage = variant === "page";

  const toolsSummary = isToolsError
    ? "Couldn't load this source's tools. A server built from it still starts with everything the source produced."
    : isLoading
      ? "Loading tools\u2026"
      : `${tools.length} tool${tools.length === 1 ? "" : "s"} generated from this source. A server built from it starts with all of them.`;

  const facts = (
    <>
      {/* The page has room for the identifiers the CLI and the upload flow
          address a source by; the sheet already names the slug in its header. */}
      {isPage && asset && (
        <>
          <SourceFact label="Slug" isPage={isPage}>
            {asset.slug}
          </SourceFact>
          <SourceFact label="Source ID" isPage={isPage}>
            <span className="inline-flex items-center gap-1">
              {asset.id}
              <CopyButton text={asset.id} size="xs" />
            </span>
          </SourceFact>
        </>
      )}
      {file && (
        <>
          <SourceFact label="File" isPage={isPage}>
            {asset?.name ?? file.id}
          </SourceFact>
          <SourceFact label="Size" isPage={isPage}>
            {formatBytes(file.contentLength)}
          </SourceFact>
          <SourceFact label="Type" isPage={isPage}>
            {file.contentType}
          </SourceFact>
          {isPage && (
            <>
              <SourceFact label="Created" isPage={isPage}>
                {dateTimeFormatters.humanize(file.createdAt)}
              </SourceFact>
              <SourceFact label="Updated" isPage={isPage}>
                {dateTimeFormatters.humanize(file.updatedAt)}
              </SourceFact>
            </>
          )}
        </>
      )}
      {functionAsset && (
        <>
          <SourceFact label="Runtime" isPage={isPage}>
            {functionAsset.runtime}
          </SourceFact>
          <SourceFact
            label="Memory"
            isPage={isPage}
            tooltip="Memory each instance of this function runs with. The default applies when the source sets none."
          >
            {formatMemory(
              functionAsset.memoryMib ?? DEFAULT_FUNCTION_MEMORY_MIB,
            )}
            {functionAsset.memoryMib == null && " (default)"}
          </SourceFact>
          <SourceFact
            label="Instances"
            isPage={isPage}
            tooltip="How many instances of this function run at once. The default applies when the source sets none."
          >
            {functionAsset.scale ?? DEFAULT_FUNCTION_SCALE}
            {functionAsset.scale == null && " (default)"}
          </SourceFact>
        </>
      )}
      {deployment?.id && (
        <SourceFact
          label="Active deployment"
          isPage={isPage}
          tooltip="Every push creates a deployment: a version of all this project's sources and the tools generated from them. This is the newest one, and what the dashboard reads from."
        >
          <routes.deployments.deployment.Link params={[deployment.id]}>
            {deployment.id}
          </routes.deployments.deployment.Link>
        </SourceFact>
      )}
    </>
  );

  // The page gets the app's dashboard cards: a titled, bordered panel with
  // its rows divided inside it, rather than facts floating on white.
  if (isPage) {
    return (
      <div className="flex flex-col gap-6">
        {(file || deployment?.id) && (
          <Card.Dashboard title="Details" bodyClassName="p-0">
            <dl className="divide-border divide-y">{facts}</dl>
          </Card.Dashboard>
        )}

        {/* Where the source is used is what someone landing here is usually
            after; the tools, activity and versions follow as sections of
            their own. */}
        <SourceServersPanel toolUrns={toolUrns} isPage />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="neutral">
            <Badge.Text>
              {sourceKind === "openapi" ? "OpenAPI document" : "Function"}
            </Badge.Text>
          </Badge>
          {asset?.slug && (
            <Text small muted className="font-mono">
              {asset.slug}
            </Text>
          )}
        </div>
        {(file || deployment?.id) && (
          <dl className="border-foreground/10 mt-1 flex flex-col gap-2 border-t pt-3">
            {facts}
          </dl>
        )}
        <Text small muted>
          {toolsSummary}
        </Text>
      </div>

      {tools.length > 0 && (
        <div className="flex flex-col gap-3">
          <Text className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            Tools
          </Text>
          {/* A source's dozens of tools would push the file details off screen
              in the sheet, so the list scrolls in place here. */}
          <div className="max-h-96 overflow-y-auto">
            {tools.map((tool) => (
              <div
                key={tool.toolUrn}
                className="border-foreground/10 flex flex-col gap-1 border-b py-3 last:border-b-0"
              >
                <Text small className="font-mono">
                  {tool.name}
                </Text>
                {tool.description && (
                  <Text small muted className="line-clamp-2">
                    {tool.description}
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <SourceServersPanel toolUrns={toolUrns} isPage={false} />

      {!isLoading && !isToolsError && tools.length === 0 && (
        <Text small muted>
          This source has produced no tools yet. A server built from it starts
          empty, and picks them up on the next deployment.
        </Text>
      )}
    </div>
  );
}

/**
 * The panel header's download action, rendered by the side panel rather than
 * by the body above.
 *
 * Resolves the source from the same two queries the body uses, which the page
 * has already warmed, so it costs nothing to look the asset up a second time
 * here instead of threading it through the (deliberately serializable) panel
 * descriptor.
 */
export function SourceDownloadButton({
  sourceKind,
  assetId,
  variant = "chip",
}: {
  sourceKind: "openapi" | "function";
  assetId: string;
  /**
   * "chip" is the small bordered control the panel header wears beside Docs
   * and Close; "button" is the app's ordinary secondary button, for the
   * page's action row.
   */
  variant?: "chip" | "button";
}): React.JSX.Element | null {
  const project = useProject();
  const { projectSlug } = useSlugs();
  const [isDownloading, setIsDownloading] = useState(false);
  const { data: deploymentResult } = useActiveDeployment();
  const { data: assetsResult } = useListAssets();

  const isOpenAPI = sourceKind === "openapi";
  const deployment = deploymentResult?.deployment;
  const asset = isOpenAPI
    ? deployment?.openapiv3Assets?.find((a) => a.id === assetId)
    : deployment?.functionsAssets?.find((a) => a.id === assetId);
  const file = assetsResult?.assets?.find((a) => a.id === asset?.assetId);

  if (!asset?.assetId) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadSourceFile({
        assetId: asset.assetId,
        projectId: project.id,
        projectSlug,
        isOpenAPI,
        filename: (blob) =>
          sourceDownloadFilename(
            {
              kind: sourceKind,
              name: asset.name,
              contentType: file?.contentType,
            },
            blob,
          ),
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `Couldn't download this source: ${error.message}`
          : "Couldn't download this source",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const label = isDownloading ? "Downloading" : "Download";

  if (variant === "button") {
    return (
      <Button
        type="button"
        variant="secondary"
        disabled={isDownloading}
        onClick={() => {
          void handleDownload();
        }}
      >
        <Button.Text>{label}</Button.Text>
        <Button.RightIcon>
          {isDownloading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
        </Button.RightIcon>
      </Button>
    );
  }

  return (
    <button
      type="button"
      disabled={isDownloading}
      onClick={() => {
        void handleDownload();
      }}
      className="text-muted-foreground hover:text-foreground bg-muted/40 hover:bg-muted flex items-center gap-1.5 border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-60"
    >
      {label}
      {isDownloading ? (
        <Loader2 className="size-3 animate-spin" />
      ) : (
        <Download className="size-3" />
      )}
    </button>
  );
}
